import classNames from "classnames";
import { Trans } from "react-i18next";
import type { ReactNode } from "react";
import { isNotNil } from "@kalodata/tools";
import { twMerge } from "tailwind-merge";

export type ClipTabValue = string | number;

export interface ClipTabItem<T extends ClipTabValue = ClipTabValue> {
  value: T;
  label: ReactNode;
  icon?: ReactNode | ((isActive: boolean) => ReactNode);
  disabled?: boolean;
  children?: ReactNode;
}

export interface ClipTabsProps<T extends ClipTabValue = ClipTabValue> {
  value: T;
  items: ClipTabItem<T>[];
  onChange: (value: T) => void;
  className?: string;
  tabListClassName?: string;
  tabPanelClassName?: string;
}

export const ClipTabs = <T extends ClipTabValue = ClipTabValue>(props: ClipTabsProps<T>) => {
  const { value, items, onChange, className, tabListClassName, tabPanelClassName } = props;
  const activeItem = items.find((item) => item.value === value);

  return (
    <div className={twMerge("flex flex-col items-stretch", className)}>
      <div
        role="tablist"
        className={twMerge(
          "flex items-center gap-[8px] h-[56px] px-[16px] border-b border-kds-border",
          tabListClassName,
        )}
      >
        {items.map((item) => {
          const isActive = item.value === value;
          const iconNode = typeof item.icon === "function" ? item.icon(isActive) : item.icon;

          return (
            <button
              key={String(item.value)}
              type="button"
              role="tab"
              aria-selected={isActive}
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled || isActive) return;
                onChange(item.value);
              }}
              className={classNames(
                "flex items-center justify-center py-[12px] cursor-pointer border-b-2 border-solid",
                isActive ? "border-kds-dark-reverse" : "border-transparent",
                item.disabled && "cursor-not-allowed opacity-50",
              )}
            >
              <div
                className={classNames(
                  "flex items-center justify-center gap-[6px] h-[32px] px-[12px] py-[6px] rounded-[8px] border border-solid transition-colors  hover:bg-kds-bg-overlay",
                  isActive
                    ? "border-kds-border-solid text-kds-fg"
                    : "border-transparent text-kds-fg-soft hover:text-kds-fg",
                )}
              >
                {iconNode}
                <span className="text-[14px] leading-[16px] font-medium whitespace-nowrap">
                  {typeof item.label === "string" ? <Trans>{item.label}</Trans> : item.label}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {isNotNil(activeItem?.children) && (
        <div role="tabpanel" key={String(activeItem.value)} className={tabPanelClassName}>
          {activeItem.children}
        </div>
      )}
    </div>
  );
};
